/**
 * The builder, once, for every kind of form.
 *
 * The backend already made this move: customers and visits are the same
 * versioned form with the same three invariants, built by one factory.
 * Keeping two copies of the builder screen would have re-created exactly
 * the drift that refactor removed — a fix applied to one and forgotten
 * in the other.
 *
 * Everything that differs between the two forms is here.
 */
export interface FormKind {
  /** API prefix: `${basePath}/form`, `/form/draft`, `/form/publish`. */
  basePath: string;
  title: string;
  subtitle: (version: number) => string;
  /** What one stored row is called, in the removal warning. */
  recordNoun: string;
  recordNounPlural: string;
  /** Where `showInList` puts the field. */
  listLabel: string;
  /** Why a system field cannot be removed. Empty when there are none. */
  systemFieldNote: string;
  /** The phone preview's header and button. */
  preview: { title: string; cta: string };
  /** A visit form with no custom fields at all is a legitimate choice. */
  allowEmpty: boolean;
  /** Must match maxFields in the matching server-side draft schema. */
  maxFields: number;
}

export const FORM_KINDS: Record<'customer' | 'visit' | 'lead', FormKind> = {
  customer: {
    basePath: '/customers',
    title: 'Customer form',
    subtitle: (v) =>
      `Version ${v} is live. Build the form your reps fill in when they add a shop.`,
    recordNoun: 'customer',
    recordNounPlural: 'customers',
    listLabel: 'Show as a column in the customer list',
    systemFieldNote:
      'Every customer needs a name and an area, so this field stays on the form.',
    preview: { title: 'New Customer', cta: 'Save customer' },
    allowEmpty: false,
    maxFields: 60,
  },

  lead: {
    basePath: '/leads',
    title: 'Lead form',
    subtitle: (v) =>
      `Version ${v} is live. Build the form your team fills in when they add a lead.`,
    recordNoun: 'lead',
    recordNounPlural: 'leads',
    listLabel: 'Show as a column in the lead list',
    systemFieldNote:
      'Every lead needs a name, so this field stays on the form.',
    preview: { title: 'New Lead', cta: 'Save lead' },
    allowEmpty: false,
    maxFields: 40,
  },

  visit: {
    basePath: '/visits',
    title: 'Visit form',
    subtitle: (v) =>
      `Version ${v} is live. This is what a rep fills in when they close a visit — `
      + 'everything else about the visit is captured automatically.',
    recordNoun: 'visit',
    recordNounPlural: 'visits',
    listLabel: 'Show in the visit list',
    // A visit is already complete without a single custom field, so
    // there is nothing here that cannot be removed.
    systemFieldNote: '',
    preview: { title: 'End visit', cta: 'Save visit' },
    allowEmpty: true,
    // Lower than the customer form on purpose: a customer form is filled
    // in once, a visit form twenty times a day at a counter.
    maxFields: 20,
  },
};
